import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  IconButton,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Divider,
  Snackbar,
  Tooltip,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Business as ClubIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Language as WebsiteIcon,
  LocationOn as LocationIcon,
  Person as PersonIcon,
  CalendarMonth as CalendarIcon,
  IceSkating as SkaterIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  WarningAmber as WarningAmberIcon,
} from '@mui/icons-material';
import { useFirebaseAuth } from '../context/FirebaseAuthContext';
import { useImpersonation } from '../context/ImpersonationContext';
import { getApiBaseUrlWithFallback } from '../config/environment';

const emptyFormData = {
  name: '',
  shortName: '',
  aliases: '',
  website: '',
  isActive: true,
  location: {
    address: '',
    city: '',
    state: '',
    zipCode: '',
  },
  contact: {
    name: '',
    email: '',
    phone: '',
  },
};

export default function InterclubClubs({ interclubId, interclub }) {
  const { getIdToken } = useFirebaseAuth();
  const { getHeaders: getImpersonationHeaders } = useImpersonation();
  const [searchParams, setSearchParams] = useSearchParams();
  const scopeMyOnly = searchParams.get('scope') === 'my';

  const [clubs, setClubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Dialog states
  const [selectedClub, setSelectedClub] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [formData, setFormData] = useState(emptyFormData);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [sortField, setSortField] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [unrecognized, setUnrecognized] = useState([]);
  const [unrecognizedSeason, setUnrecognizedSeason] = useState('');
  const [unrecognizedLoading, setUnrecognizedLoading] = useState(false);
  const [unrecognizedError, setUnrecognizedError] = useState(null);
  const [showUnrecognized, setShowUnrecognized] = useState(false);
  const [aliasDialogOpen, setAliasDialogOpen] = useState(false);
  const [aliasTargetClubId, setAliasTargetClubId] = useState('');
  const [aliasSource, setAliasSource] = useState(null);

  const loadClubs = useCallback(async () => {
    if (!interclubId) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const baseUrl = await getApiBaseUrlWithFallback();
      const seriesParam = scopeMyOnly ? '' : '?series=true';
      const headers = {};
      if (seriesParam) {
        const token = await getIdToken();
        if (token) headers.Authorization = `Bearer ${token}`;
      }
      const response = await fetch(`${baseUrl}/interclubs/${interclubId}/clubs${seriesParam}`, { headers });
      
      if (!response.ok) {
        throw new Error('Failed to fetch clubs');
      }
      
      const data = await response.json();
      setClubs(data || []);
    } catch (err) {
      console.error('Error loading clubs:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [interclubId, scopeMyOnly, getIdToken]);

  const loadUnrecognizedClubs = useCallback(async () => {
    if (!interclubId) return;
    try {
      setUnrecognizedLoading(true);
      setUnrecognizedError(null);
      const token = await getIdToken();
      const baseUrl = await getApiBaseUrlWithFallback();
      const params = new URLSearchParams();
      if (!scopeMyOnly) params.set('series', 'true');
      const response = await fetch(`${baseUrl}/interclubs/${interclubId}/unrecognized-clubs?${params.toString()}`, {
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...getImpersonationHeaders(),
        },
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to load unrecognized clubs');
      }
      const data = await response.json();
      setUnrecognized(data.unrecognized || []);
      setUnrecognizedSeason(data.seasonName || '');
    } catch (err) {
      console.error('Error loading unrecognized clubs:', err);
      setUnrecognized([]);
      setUnrecognizedError(err.message);
    } finally {
      setUnrecognizedLoading(false);
    }
  }, [interclubId, scopeMyOnly, getIdToken, getImpersonationHeaders]);

  useEffect(() => {
    loadClubs();
  }, [loadClubs]);

  useEffect(() => {
    loadUnrecognizedClubs();
  }, [loadUnrecognizedClubs]);

  // Helper to format location
  const formatLocation = (location) => {
    if (!location) return null;
    if (typeof location === 'string') return location;
    if (typeof location === 'object') {
      const parts = [location.city, location.state].filter(Boolean);
      return parts.length > 0 ? parts.join(', ') : null;
    }
    return null;
  };

  const formatFullAddress = (location) => {
    if (!location || typeof location !== 'object') return null;
    const parts = [
      location.address,
      location.city,
      location.state,
      location.zipCode,
    ].filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : null;
  };

  const formatDate = (dateValue) => {
    if (!dateValue) return '—';
    let date;
    if (dateValue._seconds) {
      date = new Date(dateValue._seconds * 1000);
    } else if (dateValue.seconds) {
      date = new Date(dateValue.seconds * 1000);
    } else {
      date = new Date(dateValue);
    }
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const handleClubClick = (club) => {
    setSelectedClub(club);
    setDetailDialogOpen(true);
  };

  const handleCloseDetail = () => {
    setDetailDialogOpen(false);
    setSelectedClub(null);
  };

  const handleAddNew = (prefillName = '') => {
    setFormData({
      ...emptyFormData,
      name: typeof prefillName === 'string' ? prefillName : '',
    });
    setSelectedClub(null);
    setEditDialogOpen(true);
  };

  const handleAddFromUnrecognized = (name) => {
    handleAddNew(name === '(blank club name)' ? '' : name);
  };

  const handleOpenAliasDialog = (entry) => {
    setAliasSource(entry);
    setAliasTargetClubId('');
    setAliasDialogOpen(true);
  };

  const handleSaveAlias = async () => {
    const club = clubs.find((c) => c.id === aliasTargetClubId);
    const aliasName = aliasSource?.name;
    if (!club || !aliasName || aliasName === '(blank club name)') {
      setSnackbar({ open: true, message: 'Choose an existing club to attach this name as an alias.', severity: 'error' });
      return;
    }
    try {
      setSaving(true);
      const token = await getIdToken();
      const baseUrl = await getApiBaseUrlWithFallback();
      const aliases = Array.isArray(club.aliases) ? [...club.aliases] : [];
      if (!aliases.some((a) => String(a).trim().toLowerCase() === aliasName.trim().toLowerCase())) {
        aliases.push(aliasName.trim());
      }
      const response = await fetch(`${baseUrl}/clubs/${club.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          ...getImpersonationHeaders(),
        },
        body: JSON.stringify({
          name: club.name,
          shortName: club.shortName || '',
          aliases,
          website: club.website || '',
          isActive: club.isActive !== false,
          location: club.location || {},
          contact: club.contact || {},
          interclubId: club.interclubId || interclubId,
        }),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to add alias');
      }
      setSnackbar({ open: true, message: `Added “${aliasName}” as an alias of ${club.name}.`, severity: 'success' });
      setAliasDialogOpen(false);
      setAliasSource(null);
      loadClubs();
      loadUnrecognizedClubs();
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (club) => {
    setSelectedClub(club);
    setFormData({
      name: club.name || '',
      shortName: club.shortName || '',
      aliases: Array.isArray(club.aliases) ? club.aliases.join(', ') : '',
      website: club.website || '',
      isActive: club.isActive !== false,
      location: {
        address: club.location?.address || '',
        city: club.location?.city || '',
        state: club.location?.state || '',
        zipCode: club.location?.zipCode || '',
      },
      contact: {
        name: club.contact?.name || '',
        email: club.contact?.email || '',
        phone: club.contact?.phone || '',
      },
    });
    setDetailDialogOpen(false);
    setEditDialogOpen(true);
  };

  const handleCloseEdit = () => {
    setEditDialogOpen(false);
    setSelectedClub(null);
    setFormData(emptyFormData);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      setSnackbar({ open: true, message: 'Club name is required', severity: 'error' });
      return;
    }

    try {
      setSaving(true);
      const token = await getIdToken();
      const baseUrl = await getApiBaseUrlWithFallback();
      
      // Convert aliases string to array
      const aliasesArray = formData.aliases
        ? formData.aliases.split(',').map(a => a.trim()).filter(Boolean)
        : [];
      
      const clubData = {
        ...formData,
        aliases: aliasesArray,
        interclubId,
      };

      const url = selectedClub 
        ? `${baseUrl}/clubs/${selectedClub.id}`
        : `${baseUrl}/clubs`;
      
      const response = await fetch(url, {
        method: selectedClub ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          ...getImpersonationHeaders(),
        },
        body: JSON.stringify(clubData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to save club');
      }

      setSnackbar({ 
        open: true, 
        message: selectedClub ? 'Club updated successfully' : 'Club created successfully', 
        severity: 'success' 
      });
      handleCloseEdit();
      loadClubs();
      loadUnrecognizedClubs();
    } catch (err) {
      console.error('Error saving club:', err);
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = (club) => {
    setSelectedClub(club);
    setDetailDialogOpen(false);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!selectedClub) return;

    try {
      setSaving(true);
      const token = await getIdToken();
      const baseUrl = await getApiBaseUrlWithFallback();
      
      const response = await fetch(`${baseUrl}/clubs/${selectedClub.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete club');
      }

      setSnackbar({ open: true, message: 'Club deleted successfully', severity: 'success' });
      setDeleteDialogOpen(false);
      setSelectedClub(null);
      loadClubs();
      loadUnrecognizedClubs();
    } catch (err) {
      console.error('Error deleting club:', err);
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const filteredClubs = clubs.filter(club => {
    const searchLower = searchTerm.toLowerCase();
    const locationStr = formatLocation(club.location) || '';
    return (
      club.name?.toLowerCase().includes(searchLower) ||
      club.shortName?.toLowerCase().includes(searchLower) ||
      locationStr.toLowerCase().includes(searchLower) ||
      club.contact?.name?.toLowerCase().includes(searchLower)
    );
  });

  const sortedClubs = useMemo(() => {
    const dir = sortDirection === 'asc' ? 1 : -1;
    return [...filteredClubs].sort((a, b) => {
      let aVal, bVal;
      switch (sortField) {
        case 'name':
          aVal = (a.name || '').toLowerCase();
          bVal = (b.name || '').toLowerCase();
          return dir * (aVal < bVal ? -1 : aVal > bVal ? 1 : 0);
        case 'location':
          aVal = (formatLocation(a.location) || 'zzz').toLowerCase();
          bVal = (formatLocation(b.location) || 'zzz').toLowerCase();
          return dir * (aVal < bVal ? -1 : aVal > bVal ? 1 : 0);
        case 'skaters':
          aVal = a.skaterCount ?? 0;
          bVal = b.skaterCount ?? 0;
          return dir * (aVal - bVal);
        case 'contact':
          aVal = (a.contact?.name || '').toLowerCase();
          bVal = (b.contact?.name || '').toLowerCase();
          return dir * (aVal < bVal ? -1 : aVal > bVal ? 1 : 0);
        case 'status':
          aVal = a.isActive !== false ? 'active' : 'inactive';
          bVal = b.isActive !== false ? 'active' : 'inactive';
          return dir * (aVal < bVal ? -1 : aVal > bVal ? 1 : 0);
        case 'interclub':
          aVal = (a.interclubName || '').toLowerCase();
          bVal = (b.interclubName || '').toLowerCase();
          return dir * (aVal < bVal ? -1 : aVal > bVal ? 1 : 0);
        default:
          return 0;
      }
    });
  }, [filteredClubs, sortField, sortDirection]);

  const handleSort = (field) => {
    setSortField(field);
    setSortDirection((prev) => (prev === 'asc' && sortField === field ? 'desc' : 'asc'));
  };

  const showInterclubColumn = useMemo(() => clubs.some((c) => c.interclubName), [clubs]);

  // Calculate stats
  const stats = {
    total: clubs.length,
    active: clubs.filter(c => c.isActive !== false).length,
    inactive: clubs.filter(c => c.isActive === false).length,
    totalSkaters: clubs.reduce((sum, c) => sum + (c.skaterCount || 0), 0),
  };

  return (
    <>
      <Paper elevation={0} sx={{ p: 3, borderRadius: 4, border: '1px solid', borderColor: 'divider' }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ 
              p: 1.5, 
              borderRadius: 2, 
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              display: 'flex',
              boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)'
            }}>
              <ClubIcon sx={{ color: 'white', fontSize: 28 }} />
            </Box>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                Clubs
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {scopeMyOnly ? 'Clubs in your interclub only' : 'Clubs across all interclubs in the series'}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <ToggleButtonGroup
              value={scopeMyOnly ? 'my' : 'all'}
              exclusive
              onChange={(_, value) => {
                if (value == null) return;
                const next = new URLSearchParams(searchParams);
                if (value === 'my') next.set('scope', 'my');
                else next.delete('scope');
                setSearchParams(next);
              }}
              size="small"
              sx={{ mr: 1 }}
            >
              <ToggleButton value="all">All interclubs</ToggleButton>
              <ToggleButton value="my">{interclub?.name ? `${interclub.name} only` : 'My interclub only'}</ToggleButton>
            </ToggleButtonGroup>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddNew}
              sx={{ borderRadius: 2 }}
            >
              Add Club
            </Button>
            <IconButton 
              onClick={() => {
                loadClubs();
                loadUnrecognizedClubs();
              }} 
              sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}
            >
              <RefreshIcon />
            </IconButton>
          </Box>
        </Box>

        {/* Stats */}
        <Box sx={{ display: 'flex', gap: 1.5, mb: 3, flexWrap: 'wrap' }}>
          <Chip 
            label={`Total Clubs: ${stats.total}`} 
            variant="outlined"
            sx={{ fontWeight: 600 }}
          />
          <Chip 
            label={`Active: ${stats.active}`} 
            sx={{ bgcolor: 'success.50', color: 'success.dark', fontWeight: 600, border: '1px solid', borderColor: 'success.200' }}
          />
          {stats.inactive > 0 && (
            <Chip 
              label={`Inactive: ${stats.inactive}`} 
              variant="outlined"
              sx={{ fontWeight: 600 }}
            />
          )}
          <Chip 
            label={`Total Skaters: ${stats.totalSkaters}`} 
            sx={{ bgcolor: 'info.50', color: 'info.dark', fontWeight: 600, border: '1px solid', borderColor: 'info.200' }}
          />
        </Box>

        {/* Search */}
        <Box sx={{ mb: 3 }}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Search clubs..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
            {error}
            <Button onClick={loadClubs} sx={{ ml: 2 }} size="small">
              Retry
            </Button>
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : filteredClubs.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6, px: 3, bgcolor: 'grey.50', borderRadius: 3 }}>
            <ClubIcon sx={{ fontSize: 48, color: 'grey.400', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {searchTerm ? 'No clubs found' : 'No clubs yet'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {searchTerm ? 'Try adjusting your search terms.' : 'Add clubs to your interclub to get started.'}
            </Typography>
            {!searchTerm && (
              <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddNew}>
                Add First Club
              </Button>
            )}
          </Box>
        ) : (
          <TableContainer sx={{ 
            border: '1px solid', 
            borderColor: 'divider', 
            borderRadius: 2,
            '& .MuiTableCell-head': { bgcolor: 'grey.50', fontWeight: 700 }
          }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sortDirection={sortField === 'name' ? sortDirection : false}>
                    <TableSortLabel
                      active={sortField === 'name'}
                      direction={sortField === 'name' ? sortDirection : 'asc'}
                      onClick={() => handleSort('name')}
                    >
                      Club Name
                    </TableSortLabel>
                  </TableCell>
                  {showInterclubColumn && (
                    <TableCell sortDirection={sortField === 'interclub' ? sortDirection : false}>
                      <TableSortLabel
                        active={sortField === 'interclub'}
                        direction={sortField === 'interclub' ? sortDirection : 'asc'}
                        onClick={() => handleSort('interclub')}
                      >
                        Interclub
                      </TableSortLabel>
                    </TableCell>
                  )}
                  <TableCell sortDirection={sortField === 'location' ? sortDirection : false}>
                    <TableSortLabel
                      active={sortField === 'location'}
                      direction={sortField === 'location' ? sortDirection : 'asc'}
                      onClick={() => handleSort('location')}
                    >
                      Location
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="center" sortDirection={sortField === 'skaters' ? sortDirection : false}>
                    <TableSortLabel
                      active={sortField === 'skaters'}
                      direction={sortField === 'skaters' ? sortDirection : 'asc'}
                      onClick={() => handleSort('skaters')}
                    >
                      Skaters
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={sortField === 'contact' ? sortDirection : false}>
                    <TableSortLabel
                      active={sortField === 'contact'}
                      direction={sortField === 'contact' ? sortDirection : 'asc'}
                      onClick={() => handleSort('contact')}
                    >
                      Contact
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={sortField === 'status' ? sortDirection : false}>
                    <TableSortLabel
                      active={sortField === 'status'}
                      direction={sortField === 'status' ? sortDirection : 'asc'}
                      onClick={() => handleSort('status')}
                    >
                      Status
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedClubs.map((club) => (
                  <TableRow 
                    key={club.id} 
                    hover 
                    onClick={() => handleClubClick(club)}
                    sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'primary.50' } }}
                  >
                    <TableCell>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                          {club.name}
                        </Typography>
                        {club.shortName && (
                          <Typography variant="caption" color="text.secondary">
                            {club.shortName}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    {showInterclubColumn && (
                      <TableCell>{club.interclubName || '—'}</TableCell>
                    )}
                    <TableCell>
                      <Typography variant="body2">
                        {formatLocation(club.location) || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        icon={<SkaterIcon sx={{ fontSize: 16 }} />}
                        label={club.skaterCount || 0}
                        size="small"
                        sx={{
                          fontWeight: 600,
                          bgcolor: club.skaterCount > 0 ? 'info.100' : 'grey.100',
                          color: club.skaterCount > 0 ? 'info.dark' : 'text.secondary',
                          '& .MuiChip-icon': { 
                            color: club.skaterCount > 0 ? 'info.main' : 'grey.500' 
                          },
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Box>
                        {club.contact?.name && (
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {club.contact.name}
                          </Typography>
                        )}
                        {club.contact?.email && (
                          <Typography variant="caption" color="text.secondary">
                            {club.contact.email}
                          </Typography>
                        )}
                        {!club.contact?.name && !club.contact?.email && '—'}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={club.isActive !== false ? 'Active' : 'Inactive'}
                        size="small"
                        sx={{
                          fontWeight: 600,
                          bgcolor: club.isActive !== false ? 'success.main' : 'grey.400',
                          color: 'white',
                        }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                        <Tooltip title="Edit">
                          <IconButton 
                            size="small" 
                            onClick={(e) => { e.stopPropagation(); handleEdit(club); }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton 
                            size="small" 
                            color="error"
                            onClick={(e) => { e.stopPropagation(); handleDeleteClick(club); }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Showing {sortedClubs.length} of {stats.total} club{stats.total !== 1 ? 's' : ''}
          </Typography>
        </Box>
      </Paper>

      <Paper
        elevation={0}
        onClick={() => setShowUnrecognized((v) => !v)}
        sx={{
          mt: 2,
          p: 2,
          border: '1px solid',
          borderColor: unrecognized.length ? 'warning.light' : 'divider',
          borderRadius: 2,
          cursor: 'pointer',
          bgcolor: unrecognized.length ? 'warning.50' : 'grey.50',
        }}
      >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <WarningAmberIcon color={unrecognized.length ? 'warning' : 'disabled'} />
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  Clubs in results not on this list
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Names from {unrecognizedSeason || 'the current season'} that did not match a club or alias
                  {unrecognizedLoading ? '…' : ` (${unrecognized.length} found)`}
                </Typography>
              </Box>
            </Box>
            <IconButton size="small">
              {showUnrecognized ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
        </Paper>

        {showUnrecognized && (
          <Paper
            elevation={0}
            sx={{
              mt: 1,
              p: 2,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
            }}
          >
            {unrecognizedLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress size={24} />
              </Box>
            ) : unrecognizedError ? (
              <Alert severity="error" sx={{ borderRadius: 2 }}>
                {unrecognizedError}
              </Alert>
            ) : unrecognized.length === 0 ? (
              <Alert severity="success" sx={{ borderRadius: 2 }}>
                Every club name on this season&apos;s results matches a club or alias.
              </Alert>
            ) : (
              <>
                <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
                  These names appeared on live scores or uploads but are not in your club list. Add a missing
                  club, or attach the name as an alias if it is a spelling or abbreviation of a club you already have.
                </Alert>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>Name on results</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Results</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Skaters found</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Competitions</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Possible match</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {unrecognized.map((entry) => (
                        <TableRow key={entry.normalizedKey}>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {entry.name}
                            </Typography>
                            {entry.spellings?.length > 1 && (
                              <Typography variant="caption" color="text.secondary">
                                Also: {entry.spellings.filter((s) => s !== entry.name).join(', ')}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>{entry.resultCount}</TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 600, mb: entry.skaterCount ? 0.5 : 0 }}>
                              {entry.skaterCount}
                            </Typography>
                            {((entry.skaters && entry.skaters.length > 0
                              ? entry.skaters
                              : (entry.sampleSkaters || []).map((name) => ({ name }))
                            ).slice(0, 25)).map((skater) => (
                              <Typography key={skater.name} variant="caption" display="block" color="text.secondary">
                                {skater.name}
                                {skater.competitions?.length ? ` · ${skater.competitions.join(', ')}` : ''}
                              </Typography>
                            ))}
                            {entry.skaterCount > 25 && (
                              <Typography variant="caption" color="text.secondary">
                                +{entry.skaterCount - 25} more
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption">
                              {(entry.competitions || []).join(', ') || '—'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {(entry.possibleMatches || []).length
                              ? entry.possibleMatches.join(', ')
                              : '—'}
                          </TableCell>
                          <TableCell align="right">
                            <Button size="small" onClick={() => handleAddFromUnrecognized(entry.name)}>
                              Add club
                            </Button>
                            {entry.name !== '(blank club name)' && (
                              <Button size="small" onClick={() => handleOpenAliasDialog(entry)}>
                                Add alias
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
          </Paper>
        )}

      {/* Club Detail Dialog */}
      <Dialog 
        open={detailDialogOpen} 
        onClose={handleCloseDetail}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        {selectedClub && (
          <>
            <DialogTitle sx={{ pb: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ 
                    p: 1.5, 
                    borderRadius: 2, 
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    display: 'flex',
                  }}>
                    <ClubIcon sx={{ color: 'white', fontSize: 28 }} />
                  </Box>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      {selectedClub.name}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                      <Chip
                        label={selectedClub.isActive !== false ? 'Active' : 'Inactive'}
                        size="small"
                        sx={{
                          fontWeight: 600,
                          bgcolor: selectedClub.isActive !== false ? 'success.main' : 'grey.400',
                          color: 'white',
                        }}
                      />
                      {selectedClub.shortName && (
                        <Chip label={selectedClub.shortName} size="small" variant="outlined" />
                      )}
                      <Chip
                        icon={<SkaterIcon sx={{ fontSize: 16 }} />}
                        label={`${selectedClub.skaterCount || 0} Skaters`}
                        size="small"
                        sx={{
                          fontWeight: 600,
                          bgcolor: 'info.100',
                          color: 'info.dark',
                          '& .MuiChip-icon': { color: 'info.main' },
                        }}
                      />
                    </Box>
                  </Box>
                </Box>
                <IconButton onClick={handleCloseDetail} size="small">
                  <CloseIcon />
                </IconButton>
              </Box>
            </DialogTitle>
            <DialogContent dividers>
              <Grid container spacing={2}>
                {/* Aliases */}
                {selectedClub.aliases && selectedClub.aliases.length > 0 && (
                  <>
                    <Grid item xs={12}>
                      <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 600 }}>
                        Also Known As
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {selectedClub.aliases.map((alias, idx) => (
                          <Chip key={idx} label={alias} size="small" variant="outlined" />
                        ))}
                      </Box>
                    </Grid>
                    <Grid item xs={12}><Divider sx={{ my: 1 }} /></Grid>
                  </>
                )}

                {/* Location */}
                <Grid item xs={12}>
                  <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 600 }}>
                    Location
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                    <LocationIcon color="action" fontSize="small" sx={{ mt: 0.5 }} />
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {formatFullAddress(selectedClub.location) || 'No address set'}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={12}><Divider sx={{ my: 1 }} /></Grid>

                {/* Contact Information */}
                <Grid item xs={12}>
                  <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 600 }}>
                    Contact Information
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PersonIcon color="action" fontSize="small" />
                    <Box>
                      <Typography variant="caption" color="text.secondary">Contact Name</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {selectedClub.contact?.name || 'Not set'}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <EmailIcon color="action" fontSize="small" />
                    <Box>
                      <Typography variant="caption" color="text.secondary">Email</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {selectedClub.contact?.email || 'Not set'}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PhoneIcon color="action" fontSize="small" />
                    <Box>
                      <Typography variant="caption" color="text.secondary">Phone</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {selectedClub.contact?.phone || 'Not set'}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                {selectedClub.website && (
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <WebsiteIcon color="action" fontSize="small" />
                      <Box>
                        <Typography variant="caption" color="text.secondary">Website</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          <a href={selectedClub.website} target="_blank" rel="noopener noreferrer">
                            {selectedClub.website}
                          </a>
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                )}

                <Grid item xs={12}><Divider sx={{ my: 1 }} /></Grid>

                {/* Details */}
                <Grid item xs={12}>
                  <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 600 }}>
                    Details
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CalendarIcon color="action" fontSize="small" />
                    <Box>
                      <Typography variant="caption" color="text.secondary">Created</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {formatDate(selectedClub.createdAt)}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CalendarIcon color="action" fontSize="small" />
                    <Box>
                      <Typography variant="caption" color="text.secondary">Last Updated</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {formatDate(selectedClub.updatedAt)}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
              <Button onClick={handleCloseDetail} variant="outlined" sx={{ borderRadius: 2 }}>
                Close
              </Button>
              <Button 
                variant="contained" 
                color="error"
                onClick={() => handleDeleteClick(selectedClub)}
                sx={{ borderRadius: 2 }}
              >
                Delete
              </Button>
              <Button 
                variant="contained" 
                onClick={() => handleEdit(selectedClub)}
                startIcon={<EditIcon />}
                sx={{ borderRadius: 2 }}
              >
                Edit
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Edit/Create Dialog */}
      <Dialog 
        open={editDialogOpen} 
        onClose={handleCloseEdit}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ 
                p: 1.5, 
                borderRadius: 2, 
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                display: 'flex',
              }}>
                <ClubIcon sx={{ color: 'white', fontSize: 28 }} />
              </Box>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {selectedClub ? 'Edit Club' : 'Add New Club'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {selectedClub ? 'Update club information' : 'Create a new club in your interclub'}
                </Typography>
              </Box>
            </Box>
            <IconButton onClick={handleCloseEdit} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2.5}>
            {/* Basic Info */}
            <Grid item xs={12}>
              <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 600 }}>
                Basic Information
              </Typography>
            </Grid>
            <Grid item xs={12} sm={8}>
              <TextField
                fullWidth
                label="Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                size="small"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Short Name"
                value={formData.shortName}
                onChange={(e) => setFormData({ ...formData, shortName: e.target.value })}
                size="small"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Aliases"
                value={formData.aliases}
                onChange={(e) => setFormData({ ...formData, aliases: e.target.value })}
                helperText="Comma-separated alternate names (LTS, rink, etc.)"
                size="small"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>

            <Grid item xs={12}><Divider sx={{ my: 0.5 }} /></Grid>

            {/* Location */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LocationIcon fontSize="small" color="action" />
                <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 600 }}>
                  Location
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="City"
                value={formData.location.city}
                onChange={(e) => setFormData({ 
                  ...formData, 
                  location: { ...formData.location, city: e.target.value } 
                })}
                size="small"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="State"
                value={formData.location.state}
                onChange={(e) => setFormData({ 
                  ...formData, 
                  location: { ...formData.location, state: e.target.value } 
                })}
                size="small"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address"
                value={formData.location.address}
                onChange={(e) => setFormData({ 
                  ...formData, 
                  location: { ...formData.location, address: e.target.value } 
                })}
                size="small"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>

            <Grid item xs={12}><Divider sx={{ my: 0.5 }} /></Grid>

            {/* Contact */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PersonIcon fontSize="small" color="action" />
                <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 600 }}>
                  Contact Information
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.contact.email}
                onChange={(e) => setFormData({ 
                  ...formData, 
                  contact: { ...formData.contact, email: e.target.value } 
                })}
                size="small"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone"
                value={formData.contact.phone}
                onChange={(e) => setFormData({ 
                  ...formData, 
                  contact: { ...formData.contact, phone: e.target.value } 
                })}
                size="small"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Website"
                value={formData.website}
                onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                placeholder="https://..."
                size="small"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    color="success"
                  />
                }
                label="Active"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleCloseEdit} variant="outlined" sx={{ borderRadius: 2 }}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={handleSave}
            disabled={saving || !formData.name.trim()}
            sx={{ borderRadius: 2 }}
          >
            {saving ? <CircularProgress size={24} /> : (selectedClub ? 'Save Changes' : 'Create Club')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog 
        open={deleteDialogOpen} 
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle>Delete Club?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>{selectedClub?.name}</strong>? This action cannot be undone.
          </Typography>
          <Alert severity="warning" sx={{ mt: 2, borderRadius: 2 }}>
            Clubs with competitions or skaters cannot be deleted.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDeleteDialogOpen(false)} variant="outlined" sx={{ borderRadius: 2 }}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            color="error"
            onClick={handleDelete}
            disabled={saving}
            sx={{ borderRadius: 2 }}
          >
            {saving ? <CircularProgress size={24} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={aliasDialogOpen}
        onClose={() => !saving && setAliasDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle>Add as alias</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Attach <strong>{aliasSource?.name}</strong> to an existing club so future results match that club.
          </Typography>
          <FormControl fullWidth size="small">
            <InputLabel>Existing club</InputLabel>
            <Select
              value={aliasTargetClubId}
              label="Existing club"
              onChange={(e) => setAliasTargetClubId(e.target.value)}
            >
              {clubs
                .slice()
                .sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')))
                .map((club) => (
                  <MenuItem key={club.id} value={club.id}>
                    {club.name}
                    {club.interclubName ? ` (${club.interclubName})` : ''}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setAliasDialogOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSaveAlias} disabled={saving || !aliasTargetClubId}>
            {saving ? <CircularProgress size={20} /> : 'Save alias'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          severity={snackbar.severity} 
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          sx={{ borderRadius: 2 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}
